import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDownToLine, Accessibility } from "lucide-react";
import { NAV, PARTS } from "@/data/content";
import BagButton from "@/components/BagButton";

const PORTFOLIO_PDF = "/phaedra-paulson-stylist-portfolio.pdf";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const path = location.pathname;
  const activePart = path.startsWith("/maker")
    ? "maker"
    : path.startsWith("/kingsley") || path.startsWith("/payment")
    ? "kingsley"
    : "stylist";
  const inStylist = activePart === "stylist";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[60] transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-paper/70 backdrop-blur-xl border-b border-hairline"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-12">
          <div className="flex flex-col gap-1.5">
            <Link
              to="/"
              data-testid="nav-logo"
              className="font-display text-xl leading-none tracking-tight md:text-2xl"
            >
              Phaedra Paulson
            </Link>
            <nav aria-label="Site sections" className="flex items-center gap-2.5" data-testid="part-switcher">
              {PARTS.map((p, i) => {
                const active = activePart === p.id;
                return (
                  <span key={p.id} className="flex items-center gap-2.5">
                    {i > 0 && <span aria-hidden className="text-[0.55rem] text-clay/60">·</span>}
                    <Link
                      to={p.to}
                      aria-current={active ? "true" : undefined}
                      data-testid={`part-link-${p.id}`}
                      className={`overline text-[0.62rem] transition-colors duration-300 ${
                        active ? "text-ink" : "text-stone hover:text-ink"
                      }`}
                    >
                      {p.label}
                    </Link>
                  </span>
                );
              })}
            </nav>
          </div>

          {inStylist && (
            <nav aria-label="Main" className="hidden items-center gap-9 md:flex">
              {NAV.slice(1).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `overline flex items-baseline gap-1.5 transition-colors duration-300 ${
                      isActive ? "text-ink" : "text-stone hover:text-ink"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={`text-[0.6rem] ${isActive ? "text-clay" : "text-stone"}`}>
                        {item.index}
                      </span>
                      <span className="link-underline">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
              <a
                href={PORTFOLIO_PDF}
                download
                data-testid="nav-portfolio-download"
                className="group inline-flex items-center gap-1.5 rounded-full border border-ink/70 px-3.5 py-1.5 overline text-[0.6rem] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
              >
                <span>Portfolio</span>
                <ArrowDownToLine className="h-3 w-3 transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </nav>
          )}

          <div className="flex items-center gap-3">
            <Link to="/accessibility" data-testid="nav-accessibility-link" aria-label="Accessibility"
              title="Accessibility" className="flex h-8 w-8 items-center justify-center rounded-full border border-ink/30 text-ink transition-colors hover:bg-ink hover:text-paper">
              <Accessibility className="h-4 w-4" strokeWidth={1.6} aria-hidden />
            </Link>
            {activePart === "kingsley" && <BagButton />}
          </div>

          {inStylist && (
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex flex-col items-end gap-[6px] md:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              <span className={`h-[1.5px] bg-ink transition-all duration-300 ${open ? "w-6 translate-y-[7.5px] rotate-45" : "w-7"}`} />
              <span className={`h-[1.5px] w-7 bg-ink transition-all duration-300 ${open ? "opacity-0" : "opacity-100"}`} />
              <span className={`h-[1.5px] bg-ink transition-all duration-300 ${open ? "w-6 -translate-y-[7.5px] -rotate-45" : "w-5"}`} />
            </button>
          )}
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[55] flex flex-col justify-center bg-paper px-8 md:hidden"
          >
            <div className="noise-overlay" />
            <nav aria-label="Main" className="flex flex-col gap-2">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                    className="flex items-baseline gap-4 border-b border-hairline py-4"
                  >
                    <span className="overline text-[0.6rem] text-clay">{item.index}</span>
                    <span className="font-display text-5xl tracking-tight">{item.label}</span>
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + NAV.length * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <a
                  href={PORTFOLIO_PDF}
                  download
                  data-testid="mobile-nav-portfolio-download"
                  className="flex items-baseline gap-4 border-b border-hairline py-4"
                >
                  <span className="overline text-[0.6rem] text-clay">↓</span>
                  <span className="font-display text-5xl tracking-tight">Portfolio</span>
                </a>
              </motion.div>
              <Link to="/accessibility" data-testid="mobile-nav-accessibility" className="flex items-baseline gap-4 border-b border-hairline py-4">
                <span className="overline text-[0.6rem] text-clay">A11y</span>
                <span className="font-display text-3xl tracking-tight">Accessibility</span>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
